import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  ArrowRight,
  ExternalLink,
  Code2,
  Heart,
  Package,
  ShoppingCart,
  Lock,
  Brain,
  Megaphone,
  Scale,
  Stamp,
  Truck,
  Gamepad2,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  Rocket,
  Filter,
  Grid3X3,
  List,
  Play,
  Zap,
  Users,
  Globe,
  X,
  Eye
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Animated code lines for hero
function HeroCodeAnimation() {
  const [activeLine, setActiveLine] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLine(prev => (prev + 1) % 5);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  const codeLines = [
    { text: 'import { BosphorusLabs } from "future"', color: 'text-purple-400' },
    { text: 'const products = await getProducts()', color: 'text-blue-400' },
    { text: 'products.map(p => p.innovate())', color: 'text-green-400' },
    { text: '// AI-powered solutions', color: 'text-gray-500' },
    { text: 'export default success', color: 'text-yellow-400' },
  ];

  return (
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-96 hidden xl:block">
      <div className="bg-[#1a1a2e]/80 backdrop-blur rounded-xl border border-purple-500/20 p-4 font-mono text-sm transform rotate-3 hover:rotate-0 transition-transform duration-500">
        <div className="flex gap-1.5 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        {codeLines.map((line, i) => (
          <div
            key={i}
            className={`py-1 transition-all duration-300 ${activeLine === i ? 'opacity-100 translate-x-0' : 'opacity-40 -translate-x-1'}`}
          >
            <span className={line.color}>{line.text}</span>
            {activeLine === i && <span className="animate-pulse">|</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Product Preview Animation for Featured Product
function ProductPreviewAnimation() {
  const { t } = useTranslation();
  const [typing, setTyping] = useState(true);
  const [aiResponse, setAiResponse] = useState(false);

  useEffect(() => {
    const typingInterval = setInterval(() => {
      setTyping(true);
      setAiResponse(false);
      setTimeout(() => {
        setTyping(false);
        setAiResponse(true);
      }, 2000);
    }, 5000);

    return () => clearInterval(typingInterval);
  }, []);

  return (
    <div className="rounded-xl border border-purple-500/30 overflow-hidden bg-bg-card shadow-2xl shadow-purple-500/20 w-full max-w-md">
      <div className="p-3 border-b border-border-default flex items-center gap-2 bg-bg-elevated">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <span className="text-xs text-text-tertiary ml-2">Ochtopus IDE - main.ts</span>
        <div className="ml-auto flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className="text-[10px] text-green-400">{t('products.featured.aiActive')}</span>
        </div>
      </div>

      <div className="p-4 font-mono text-sm space-y-2">
        <div className="flex gap-2">
          <span className="text-gray-500 select-none">1</span>
          <span className="text-purple-400">import</span>
          <span className="text-white">{'{ useState }'}</span>
          <span className="text-purple-400">from</span>
          <span className="text-green-400">'react'</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500 select-none">2</span>
          <span className="text-gray-500">// TODO: Add user auth</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500 select-none">3</span>
          <span className="text-blue-400">const</span>
          <span className="text-yellow-400">App</span>
          <span className="text-white">= () =&gt; {'{'}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-500 select-none">4</span>
          <span className="text-white pl-4">
            {typing && (
              <span className="inline-flex gap-1 items-center text-gray-400">
                <span className="w-2 h-4 bg-purple-400 animate-pulse" />
              </span>
            )}
          </span>
        </div>
      </div>

      {/* AI Response Panel */}
      <div className={`border-t border-purple-500/30 bg-purple-500/5 transition-all duration-500 ${aiResponse ? 'p-3 opacity-100' : 'p-0 h-0 opacity-0'}`}>
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
            <Sparkles size={12} className="text-white" />
          </div>
          <div>
            <div className="text-[10px] text-purple-400 mb-1">{t('products.featured.aiSuggestion')}</div>
            <div className="text-xs text-text-secondary">
              {t('products.animation.aiSuggestionText')}
            </div>
            <div className="flex gap-2 mt-2">
              <button className="text-[10px] px-2 py-1 rounded bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors">
                {t('products.buttons.apply')}
              </button>
              <button className="text-[10px] px-2 py-1 rounded bg-white/5 text-text-tertiary hover:bg-white/10 transition-colors">
                {t('products.buttons.reject')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Translated products data
  const translatedProducts = [
    {
      id: 'ochtopus',
      name: t('products.items.ochtopus.name'),
      tagline: t('products.items.ochtopus.tagline'),
      description: t('products.items.ochtopus.description'),
      icon: Code2,
      color: 'from-purple-500 to-fuchsia-500',
      features: t('products.items.ochtopus.features', { returnObjects: true }) as string[],
      link: 'https://ochtopus.com',
      status: 'active',
      featured: true,
      category: 'developer'
    },
    {
      id: 'minder',
      name: t('products.items.minder.name'),
      tagline: t('products.items.minder.tagline'),
      description: t('products.items.minder.description'),
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      features: t('products.items.minder.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'consumer'
    },
    {
      id: 'fulfillment',
      name: t('products.items.fulfillment.name'),
      tagline: t('products.items.fulfillment.tagline'),
      description: t('products.items.fulfillment.description'),
      icon: Package,
      color: 'from-blue-500 to-cyan-500',
      features: t('products.items.fulfillment.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'business'
    },
    {
      id: 'entegrator',
      name: t('products.items.entegrator.name'),
      tagline: t('products.items.entegrator.tagline'),
      description: t('products.items.entegrator.description'),
      icon: ShoppingCart,
      color: 'from-orange-500 to-amber-500',
      features: t('products.items.entegrator.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'business'
    },
    {
      id: 'cybershield',
      name: t('products.items.cybershield.name'),
      tagline: t('products.items.cybershield.tagline'),
      description: t('products.items.cybershield.description'),
      icon: Lock,
      color: 'from-red-500 to-orange-500',
      features: t('products.items.cybershield.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'security'
    },
    {
      id: 'ochtopus-ai',
      name: t('products.items.ochtopus-ai.name'),
      tagline: t('products.items.ochtopus-ai.tagline'),
      description: t('products.items.ochtopus-ai.description'),
      icon: Brain,
      color: 'from-violet-500 to-purple-500',
      features: t('products.items.ochtopus-ai.features', { returnObjects: true }) as string[],
      status: 'development',
      category: 'ai'
    },
    {
      id: 'admaster',
      name: t('products.items.admaster.name'),
      tagline: t('products.items.admaster.tagline'),
      description: t('products.items.admaster.description'),
      icon: Megaphone,
      color: 'from-green-500 to-emerald-500',
      features: t('products.items.admaster.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'marketing'
    },
    {
      id: 'legalai',
      name: t('products.items.legalai.name'),
      tagline: t('products.items.legalai.tagline'),
      description: t('products.items.legalai.description'),
      icon: Scale,
      color: 'from-indigo-500 to-blue-500',
      features: t('products.items.legalai.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'ai'
    },
    {
      id: 'trademarkai',
      name: t('products.items.trademarkai.name'),
      tagline: t('products.items.trademarkai.tagline'),
      description: t('products.items.trademarkai.description'),
      icon: Stamp,
      color: 'from-amber-500 to-yellow-500',
      features: t('products.items.trademarkai.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'ai'
    },
    {
      id: 'freighttracker',
      name: t('products.items.freighttracker.name'),
      tagline: t('products.items.freighttracker.tagline'),
      description: t('products.items.freighttracker.description'),
      icon: Truck,
      color: 'from-sky-500 to-blue-500',
      features: t('products.items.freighttracker.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'business'
    },
    {
      id: 'gamestream',
      name: t('products.items.gamestream.name'),
      tagline: t('products.items.gamestream.tagline'),
      description: t('products.items.gamestream.description'),
      icon: Gamepad2,
      color: 'from-fuchsia-500 to-pink-500',
      features: t('products.items.gamestream.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'consumer'
    },
    {
      id: 'salestracker',
      name: t('products.items.salestracker.name'),
      tagline: t('products.items.salestracker.tagline'),
      description: t('products.items.salestracker.description'),
      icon: TrendingUp,
      color: 'from-teal-500 to-cyan-500',
      features: t('products.items.salestracker.features', { returnObjects: true }) as string[],
      status: 'active',
      category: 'business'
    }
  ];

  // Translated categories
  const translatedCategories = [
    { id: 'all', label: t('products.categories.all'), icon: Grid3X3 },
    { id: 'business', label: t('products.categories.business'), icon: TrendingUp },
    { id: 'ai', label: t('products.categories.ai'), icon: Brain },
    { id: 'developer', label: t('products.categories.developer'), icon: Code2 },
    { id: 'security', label: t('products.categories.security'), icon: Lock },
    { id: 'marketing', label: t('products.categories.marketing'), icon: Megaphone },
    { id: 'consumer', label: t('products.categories.consumer'), icon: Users },
  ];

  // Translated status labels
  const translatedStatusLabels: Record<string, { label: string; color: string; bgColor: string }> = {
    active: { label: t('products.status.active'), color: 'text-green-400', bgColor: 'bg-green-500/10 border-green-500/30' },
    development: { label: t('products.status.development'), color: 'text-amber-400', bgColor: 'bg-amber-500/10 border-amber-500/30' },
    coming: { label: t('products.status.coming'), color: 'text-blue-400', bgColor: 'bg-blue-500/10 border-blue-500/30' }
  };

  const [selectedProduct, setSelectedProduct] = useState<typeof translatedProducts[0] | null>(null);

  // Handle hash navigation for product details (only on initial load)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && hash !== 'products') {
      const product = translatedProducts.find(p => p.id === hash);
      if (product) {
        setSelectedProduct(product);
        // Clear the hash after opening modal to prevent loop
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Close modal handler that also clears hash
  const handleCloseModal = () => {
    setSelectedProduct(null);
    // Ensure hash is cleared
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const featuredProduct = translatedProducts.find(p => p.featured);
  const filteredProducts = translatedProducts.filter(p => {
    if (activeCategory === 'all') return !p.featured;
    return p.category === activeCategory && !p.featured;
  });

  const stats = [
    { value: translatedProducts.length, label: t('products.stats.totalProducts'), icon: Package },
    { value: translatedProducts.filter(p => p.status === 'active').length, label: t('products.stats.activeProducts'), icon: Zap },
    { value: translatedProducts.filter(p => p.status === 'development').length, label: t('products.stats.inDevelopment'), icon: Code2 },
    { value: '7+', label: t('products.stats.industries'), icon: Globe },
  ];

  return (
    <>
      <SEO {...SEO_CONFIG.products} />
      <div>
      {/* Hero Section with Animation */}
      <section className="section-lg pt-32 relative overflow-hidden min-h-[60vh] flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-fuchsia-500/10" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.02]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(139, 92, 246, 0.5) 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-6">
              <Sparkles size={16} className="text-purple-400" />
              <span className="text-sm font-medium text-purple-400">{t('products.hero.badge')}</span>
            </div>

            <h1 className="heading-xl mb-6">
              {t('products.hero.title')}{' '}
              <span className="bg-gradient-to-r from-purple-400 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                {t('products.hero.titleHighlight')}
              </span>
            </h1>
            <p className="text-lg text-text-secondary mb-8 max-w-2xl">
              {t('products.hero.description')}
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="#products" className="btn btn-primary">
                {t('products.buttons.exploreProducts')}
                <ArrowRight size={18} />
              </a>
              <Link to="/contact" className="btn btn-secondary">
                {t('products.buttons.requestDemo')}
                <Play size={18} />
              </Link>
            </div>
          </div>

          <HeroCodeAnimation />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 border-y border-border-default bg-bg-surface/50">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <stat.icon className="text-purple-400" size={24} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-text-primary">{stat.value}</div>
                  <div className="text-sm text-text-secondary">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Product - Ochtopus IDE */}
      {featuredProduct && (
        <section className="section relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl" />

          <div className="container relative z-10">
            <div className="card border-purple-500/30 overflow-hidden p-0 bg-gradient-to-br from-bg-card to-bg-surface">
              <div className="grid lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-sm mb-4">
                    <Sparkles size={14} className="animate-pulse" />
                    {t('products.featured.badge')}
                  </div>
                  <h2 className="heading-xl mb-4">
                    <span className="bg-gradient-to-r from-purple-400 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                      {featuredProduct.name}
                    </span>
                  </h2>
                  <p className="text-xl text-text-secondary mb-4">{featuredProduct.tagline}</p>
                  <p className="text-text-secondary mb-6">{featuredProduct.description}</p>

                  <ul className="space-y-3 mb-8">
                    {featuredProduct.features.map((feature, index) => (
                      <li
                        key={feature}
                        className="flex items-center gap-3 text-text-secondary"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 size={12} className="text-purple-400" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-3">
                    <a
                      href={featuredProduct.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary bg-gradient-to-r from-purple-500 to-fuchsia-500 border-none hover:from-purple-600 hover:to-fuchsia-600"
                    >
                      {t('products.buttons.visitWebsite')}
                      <ExternalLink size={16} />
                    </a>
                    <button
                      onClick={() => setSelectedProduct(featuredProduct)}
                      className="btn btn-secondary"
                    >
                      <Eye size={16} />
                      {t('products.buttons.viewDetails')}
                    </button>
                  </div>
                </div>

                <div className="bg-bg-surface/50 p-8 lg:p-12 flex items-center justify-center relative">
                  {/* Decorative elements */}
                  <div className="absolute top-4 right-4 w-20 h-20 bg-purple-500/10 rounded-full blur-2xl" />
                  <div className="absolute bottom-4 left-4 w-16 h-16 bg-fuchsia-500/10 rounded-full blur-2xl" />

                  <ProductPreviewAnimation />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Products Grid */}
      <section id="products" className="section bg-bg-surface relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">
              {t('products.sections.allProducts').split(' ')[0]}{' '}
              <span className="bg-gradient-to-r from-purple-400 to-fuchsia-500 bg-clip-text text-transparent">
                {t('products.sections.allProducts').split(' ').slice(1).join(' ')}
              </span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('products.sections.allProductsDesc')}
            </p>
          </div>

          {/* Filters & View Toggle */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {translatedCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                    activeCategory === cat.id
                      ? 'bg-purple-500 text-white'
                      : 'bg-bg-card border border-border-default text-text-secondary hover:border-purple-500/50 hover:text-purple-400'
                  }`}
                >
                  <cat.icon size={14} />
                  {cat.label}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex gap-2 bg-bg-card border border-border-default rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-purple-500/20 text-purple-400' : 'text-text-secondary hover:text-purple-400'}`}
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-purple-500/20 text-purple-400' : 'text-text-secondary hover:text-purple-400'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className={`${viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}`}>
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                id={product.id}
                className={`card group hover:border-purple-500/30 transition-all duration-300 ${
                  viewMode === 'list' ? 'flex gap-6 items-start' : ''
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className={`${viewMode === 'list' ? 'flex-shrink-0' : ''}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${product.color} p-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                      <product.icon className="w-full h-full text-white" />
                    </div>
                    {viewMode === 'grid' && (
                      <span className={`text-xs px-3 py-1 rounded-full border ${translatedStatusLabels[product.status].bgColor} ${translatedStatusLabels[product.status].color}`}>
                        {translatedStatusLabels[product.status].label}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="heading-md group-hover:text-purple-400 transition-colors">{product.name}</h3>
                    {viewMode === 'list' && (
                      <span className={`text-xs px-3 py-1 rounded-full border ${translatedStatusLabels[product.status].bgColor} ${translatedStatusLabels[product.status].color}`}>
                        {translatedStatusLabels[product.status].label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-purple-400 mb-3">{product.tagline}</p>
                  <p className="text-text-secondary text-sm mb-4">{product.description}</p>

                  <ul className={`space-y-2 ${viewMode === 'list' ? 'grid grid-cols-2 gap-2' : ''}`}>
                    {product.features.slice(0, viewMode === 'list' ? 4 : 3).map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs text-text-secondary">
                        <CheckCircle2 size={12} className="text-purple-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center gap-3 mt-4">
                    <button
                      onClick={() => setSelectedProduct(product)}
                      className="inline-flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      <Eye size={14} />
                      {t('products.buttons.viewDetails')}
                    </button>
                    {product.link && (
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-purple-400 transition-colors"
                      >
                        <ExternalLink size={14} />
                        {t('products.buttons.visitWebsite')}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                <Package className="text-purple-400" size={32} />
              </div>
              <h3 className="heading-sm mb-2">{t('products.empty.title')}</h3>
              <p className="text-text-secondary mb-4">{t('products.empty.description')}</p>
              <button
                onClick={() => setActiveCategory('all')}
                className="btn btn-secondary"
              >
                {t('products.buttons.showAll')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-lg relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-fuchsia-500/10" />

        {/* Floating Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-purple-400/40 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDuration: `${2 + Math.random() * 3}s`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Gradient Lines */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-purple-500/20 to-transparent" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-fuchsia-500/20 to-transparent" />
        <div className="absolute left-0 top-1/3 w-full h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
        <div className="absolute left-0 bottom-1/3 w-full h-px bg-gradient-to-r from-transparent via-fuchsia-500/20 to-transparent" />

        {/* Corner Accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-purple-500/20 rounded-tl-3xl" />
        <div className="absolute top-0 right-0 w-32 h-32 border-r-2 border-t-2 border-fuchsia-500/20 rounded-tr-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 border-l-2 border-b-2 border-fuchsia-500/20 rounded-bl-3xl" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-purple-500/20 rounded-br-3xl" />

        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/30">
              <Rocket className="text-white" size={40} />
            </div>
            <h2 className="heading-lg mb-4">
              {t('products.cta.title')}
            </h2>
            <p className="text-text-secondary mb-8 text-lg">
              {t('products.cta.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn btn-primary btn-lg">
                <Rocket size={20} />
                {t('products.buttons.getProjectQuote')}
              </Link>
              <Link to="/services" className="btn btn-secondary btn-lg">
                {t('products.buttons.ourServices')}
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div
            className="bg-bg-card border border-border-default rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-bg-card border-b border-border-default p-6 flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${selectedProduct.color} p-3 shadow-lg`}>
                  <selectedProduct.icon className="w-full h-full text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text-primary">{selectedProduct.name}</h2>
                  <p className="text-sm text-purple-400">{selectedProduct.tagline}</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-lg hover:bg-bg-elevated text-text-tertiary hover:text-text-primary transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <span className={`text-sm px-3 py-1.5 rounded-full border ${translatedStatusLabels[selectedProduct.status].bgColor} ${translatedStatusLabels[selectedProduct.status].color}`}>
                  {translatedStatusLabels[selectedProduct.status].label}
                </span>
                <span className="text-sm text-text-tertiary">
                  {t('products.modal.category')}: {translatedCategories.find(c => c.id === selectedProduct.category)?.label || selectedProduct.category}
                </span>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-medium text-text-tertiary mb-2">{t('products.modal.description')}</h3>
                <p className="text-text-secondary">{selectedProduct.description}</p>
              </div>

              {/* Features */}
              <div>
                <h3 className="text-sm font-medium text-text-tertiary mb-3">{t('products.modal.features')}</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedProduct.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-text-secondary">
                      <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 size={12} className="text-purple-400" />
                      </div>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-border-default">
                {selectedProduct.link && (
                  <a
                    href={selectedProduct.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary bg-gradient-to-r from-purple-500 to-fuchsia-500 border-none hover:from-purple-600 hover:to-fuchsia-600"
                  >
                    <ExternalLink size={16} />
                    {t('products.buttons.visitWebsite')}
                  </a>
                )}
                <Link
                  to="/contact"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  <Rocket size={16} />
                  {t('products.buttons.requestDemo')}
                </Link>
                <button
                  onClick={handleCloseModal}
                  className="btn btn-secondary ml-auto"
                >
                  {t('products.buttons.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
